import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { PaymentsFacade } from '@sona/angular-payments';
import { SonaIconComponent } from '@sona/angular-ui-components';
import { useI18n } from '@sona/angular-util-common';
import { HlmAlertImports } from '@spartan-ng/ui-alert-helm';
import { HlmButtonDirective } from '@spartan-ng/ui-button-helm';
import { Dialog, HlmDialogDescriptionDirective, HlmDialogHeaderComponent, HlmDialogOptions, HlmDialogTitleDirective } from '@spartan-ng/ui-dialog-helm';
import { HlmSkeletonComponent } from '@spartan-ng/ui-skeleton-helm';


@Component({
    selector: 'sona-purchase-starter-audits-page',
    imports: [
        TranslatePipe,
        HlmButtonDirective,
        HlmSkeletonComponent,
        HlmAlertImports,
        HlmDialogHeaderComponent,
        HlmDialogDescriptionDirective,
        HlmDialogTitleDirective,
        SonaIconComponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'tw-contents'
    },
    template: `
        <hlm-dialog-header>
            <h1 hlmDialogTitle class="tw-text-2xl sm:tw-text-3xl">
                {{ 'PAYMENTS.PAGE.STARTER_AUDITS.HEADER.TITLE' | translate }}
            </h1>
            <p hlmDialogDescription class="tw-text-sm sm:tw-text-base">
                {{ 'PAYMENTS.PAGE.STARTER_AUDITS.HEADER.SUBTITLE' | translate }}
                @if (price() != null) {
                    {{ price() }}
                } @else {
                    <hlm-skeleton class="tw-inline-block tw-h-4 tw-w-6" />
                }
            </p>
        </hlm-dialog-header>

        <div class="tw-overflow-y-auto tw-py-4 tw-border-y">
            <p class="tw-text-muted-foreground">{{ 'PAYMENTS.PAGE.STARTER_AUDITS.INTRO' | translate }}</p>

            <p class="tw-text-muted-foreground">{{ 'PAYMENTS.PAGE.STARTER_AUDITS.SETUP' | translate }}</p>

            <div class="tw-mb-4">
                <h3 class="tw-text-lg tw-font-medium tw-mb-2">{{ 'PAYMENTS.PAGE.STARTER_AUDITS.FEATURES.HEADING' | translate }}</h3>
                <ul class="tw-list-inside tw-space-y-2 tw-text-muted-foreground tw-mb-2 tw-pl-1">
                    <li class="tw-flex tw-gap-1">
                        <sona-icon class="tw-text-green-500 tw-mt-0.5" name="check-circle" appearance="fill"
                                   size="small" />
                        {{ 'PAYMENTS.PAGE.STARTER_AUDITS.FEATURES.ITEMS.0' | translate }}
                    </li>
                    <li class="tw-flex tw-gap-1">
                        <sona-icon class="tw-text-green-500 tw-mt-0.5" name="check-circle" appearance="fill"
                                   size="small" />
                        {{ 'PAYMENTS.PAGE.STARTER_AUDITS.FEATURES.ITEMS.1' | translate }}
                    </li>
                    <li class="tw-flex tw-gap-1">
                        <sona-icon class="tw-text-green-500 tw-mt-0.5" name="check-circle" appearance="fill"
                                   size="small" />
                        {{ 'PAYMENTS.PAGE.STARTER_AUDITS.FEATURES.ITEMS.2' | translate }}
                    </li>
                    <li class="tw-flex tw-gap-1">
                        <sona-icon class="tw-text-green-500 tw-mt-0.5" name="check-circle" appearance="fill"
                                   size="small" />
                        {{ 'PAYMENTS.PAGE.STARTER_AUDITS.FEATURES.ITEMS.3' | translate }}
                    </li>
                </ul>
            </div>

            <div hlmAlert variant="info">
                <span hlmAlertTitle class="tw-text-base tw-font-medium">
                    {{ 'PAYMENTS.PAGE.STARTER_AUDITS.ALERT.TITLE' | translate }}
                </span>
                <p hlmAlertDesc>{{ 'PAYMENTS.PAGE.STARTER_AUDITS.ALERT.DESCRIPTION' | translate }}</p>
            </div>
        </div>

        <div class="tw-flex tw-items-center">
            <div class="tw-text-xl tw-font-medium tw-mr-auto">
                {{ 'PAYMENTS.PAGE.STARTER_AUDITS.FOOTER.NAME' | translate }}
            </div>
            <div>
                <div class="tw-text-[2rem] tw-font-semibold tw-h-10 tw-flex tw-items-center">
                    @if (price() != null) {
                        {{ price() }}
                    } @else {
                        <hlm-skeleton class="tw-h-10 tw-w-16" />
                    }
                </div>
                <div class="tw-text-sm tw-text-muted-foreground">
                    {{ 'PAYMENTS.PAGE.STARTER_AUDITS.FOOTER.ONE_TIME' | translate }}
                </div>
            </div>
        </div>
        <button
            id="purchase-starter-audits-buy-button"
            hlmBtn
            class="tw-w-full"
            size="lg"
            [disabled]="price() == null"
            (click)="facade.openCheckout('starter-audit')"
        >
            {{ 'PAYMENTS.PAGE.STARTER_AUDITS.FOOTER.ACTION.BUY' | translate }}
        </button>
    `
})
export class SonaPurchaseStarterAuditsDialog extends Dialog {
    static override readonly baseOptions: Partial<HlmDialogOptions> = {
        panelClass: 'tw-max-h-full'
    };

    readonly facade = inject(PaymentsFacade);
    readonly i18n = useI18n();

    readonly priceResource = this.facade.getPrice('starter-audit');
    readonly price = computed(() => {
        if (!this.priceResource.hasValue()) {
            return undefined;
        }

        const price = this.priceResource.value();

        if (!price) {
            return undefined;
        }

        return Intl.NumberFormat(this.i18n().language, {
            style: 'currency',
            currency: price.currency,
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
        }).format(price.unit_amount / 100);
    });
}
